import type { MapperImageSize, MapperImageSizes, ModuleMediaSizedSource } from '#layers/core/types'
import { hexToRgba } from './hexToRgba'

/**
 * Determine if the image should use an automatic height
 */
const isHeightAuto = (height: number | string): boolean => !height || height === 'auto' || Number.isNaN(height)

/**
 * getTransformedUrl, adds resizing, cropping and zoom transformations to the url
 * @see https://developer.frontify.com/d/XFPCrGNrXQQM/asset-processing-api#/operations/resizing
 * @see https://developer.frontify.com/d/XFPCrGNrXQQM/asset-processing-api#/operations/cropping-focal-point
 *
 * @example
 * getTransformedUrl('url', { width: 300, height: 100 }, [0.3, 0.7], 150)
 */
const getTransformedUrl = (
  url: string,
  { width, height }: MapperImageSize,
  focalPoint: Array<number>,
  zoom?: number
) => {
  if (!url) return ''
  // if it's a relative URL it shouldn't be mapped
  if (url.startsWith('/')) return url
  const mappedUrl = new URL(url)

  const [focalPointX, focalPointY] = focalPoint || [0.5, 0.5]

  mappedUrl.searchParams.append('width', `${width}`)
  if (isHeightAuto(height)) {
    // resized using only width to let the height be set automatically based on the image
    return mappedUrl.toString()
  }

  mappedUrl.searchParams.append('height', `${height}`)
  mappedUrl.searchParams.append('crop', 'fp')
  mappedUrl.searchParams.append('fp', `${focalPointX},${focalPointY}`)

  // Apply zoom if it exceeds default value and doesn't exceed maximum value
  if (zoom && zoom > 100 && zoom <= 300) {
    const fpZoomParam = zoom / 100
    mappedUrl.searchParams.append('fp_zoom', `${fpZoomParam}`)
  }

  // resize the image with width and height
  return mappedUrl.toString()
}

const getTransformedUrlSVG = (url: string, recolor?: string) => {
  if (!url) {
    return ''
  }
  const mappedUrl = new URL(url)
  if (recolor) {
    mappedUrl.searchParams.append('color', `${hexToRgba(recolor)}`)
  }
  return mappedUrl.toString()
}

/**
 * Get the multiplier for large images to support retina screens
 *
 */
const getImageLargeMultiplier = (largeSizeOption: MapperImageSize, width: number, height: number) => {
  const autoHeight = isHeightAuto(largeSizeOption.height)
  const heightAsNumber = autoHeight ? 0 : (largeSizeOption.height as number)
  switch (true) {
    case width >= largeSizeOption.width * 2 && (autoHeight || height >= heightAsNumber * 2):
      // check if the image is big enough to support size*2 on large screens
      return 2
    case width >= largeSizeOption.width * 1.5 && (autoHeight || height >= heightAsNumber * 1.5):
      // check if the image is big enough to support size*1.5 on large screens
      return 1.5
    default:
      return 1
  }
}

/**
 * Mapper for Frontify images
 */
export const mapperImage = (
  image: any,
  sizes?: MapperImageSizes,
  recolor?: string,
  zoom?: number
): {
  small: ModuleMediaSizedSource
  medium: ModuleMediaSizedSource
  large: ModuleMediaSizedSource
} | null => {
  if (!image.src) return null
  const small = sizes?.small
  const medium = sizes?.medium
  const large = sizes?.large
  const imageName = image.name || ''
  const imageUrl = new URL(image.src)
  const isSVG = image.extension === 'svg'

  if (!isSVG) {
    imageUrl.searchParams.append('format', 'webp')
  }

  const hasSizes = !!small && !!medium && !!large

  // When image extension is svg or no sizes are set do not apply any transformations
  if (!hasSizes) {
    const url = imageUrl.toString()
    return {
      small: { src: url, name: imageName, aspectRatio: null },
      medium: { src: url, name: imageName, aspectRatio: null },
      large: { src: url, name: imageName, aspectRatio: null }
    }
  }

  // Multiply support retina screens
  const imageLargeMultiplier = getImageLargeMultiplier(large, image?.size?.width, image?.size?.height)
  const imageSmallSizes = {
    width: small.width * 2,
    height: typeof small.height === 'string' ? small.height : small.height * 2,
    aspectRatio: small.aspectRatio
  }
  const imageMediumSizes = {
    width: medium.width * 2,
    height: typeof medium.height === 'string' ? medium.height : medium.height * 2,
    aspectRatio: medium.aspectRatio
  }
  const imageLargeSizes = {
    width: Math.floor(large.width * imageLargeMultiplier),
    height: typeof large.height === 'string' ? large.height : Math.floor(large.height * imageLargeMultiplier),
    aspectRatio: large.aspectRatio
  }

  const shouldResize = image.frontifyType === 'Image' && image.extension !== 'svg'

  // For logos that are recolored to WHITE we want to replace the background in case it's the default light-grey for the adyen-black so it has contrast
  const lowContrastBgColor = '#f4f5f6'
  const alternativeBgColor = '#001222'
  let backgroundColor = image.color
  if (
    recolor &&
    (recolor.toLowerCase() === '#ffffff' || recolor.toLowerCase() === '#fff') &&
    image.color?.toLowerCase() === lowContrastBgColor
  ) {
    backgroundColor = alternativeBgColor
  }

  const url = imageUrl.toString()
  const smallImageSrc = isSVG
    ? getTransformedUrlSVG(url, recolor)
    : getTransformedUrl(url, imageSmallSizes, image.focalPoint, zoom)

  const mediumImageSrc = isSVG
    ? getTransformedUrlSVG(url, recolor)
    : getTransformedUrl(url, imageMediumSizes, image.focalPoint, zoom)

  const largeImageSrc = isSVG
    ? getTransformedUrlSVG(url, recolor)
    : getTransformedUrl(url, imageLargeSizes, image.focalPoint, zoom)

  return {
    small: {
      src: smallImageSrc,
      name: imageName,
      aspectRatio: shouldResize ? imageSmallSizes.aspectRatio : '',
      color: backgroundColor,
      dimensions: isSVG ? null : { width: small.width, height: small.height }
    },
    medium: {
      src: mediumImageSrc,
      name: imageName,
      aspectRatio: shouldResize ? imageMediumSizes.aspectRatio : '',
      color: backgroundColor,
      dimensions: isSVG ? null : { width: medium.width, height: medium.height }
    },
    large: {
      src: largeImageSrc,
      name: imageName,
      aspectRatio: shouldResize ? imageLargeSizes.aspectRatio : '',
      color: backgroundColor,
      dimensions: isSVG ? null : { width: large.width, height: large.height }
    }
  }
}
